/**
 * reportClientSource — the SINGLE connected-decision source (Story 37.8).
 *
 * Both BLOCKERS the story's adversarial validation caught funnel through here so
 * the route's sample-data banner and CanvasEditor's live↔mock merge can never
 * disagree (blocker #10) and a saved report opened without `?clientId=` still
 * fetches live data (blocker #9):
 *
 *  - {@link resolveEffectiveClientId} — `searchClientId ?? loadedReportClientId`.
 *    The search param is the only client id on a fresh "create report" flow, but
 *    a saved report opened from the Reports list has no `?clientId=` — so fall
 *    back to the client the report itself carries.
 *  - {@link isClientConnected} — the ONE predicate that decides connected (live
 *    numbers) vs. unconnected (sample data + banner). The route and the canvas
 *    both derive their behavior from this exact function, so there is no state
 *    where the canvas shows mock while the banner is hidden (or vice-versa).
 */

/**
 * The effective client id for the editor: the search param wins (an explicit
 * "report for THIS client" intent), else the saved report's own client (blocker
 * #9). `undefined` when neither is known yet (e.g. before the report query
 * resolves) — downstream `enabled: !!clientId` guards idle every fetch until it
 * is.
 */
export function resolveEffectiveClientId(
  searchClientId: string | undefined,
  loadedReportClientId: string | null | undefined,
): string | undefined {
  return searchClientId ?? loadedReportClientId ?? undefined;
}

/**
 * Whether the (effective) client should show LIVE data: an effective client id
 * exists AND it has ≥1 connected platform. Drives BOTH the route's banner
 * suppression and the canvas live/mock merge (blocker #10 — single source).
 */
export function isClientConnected(
  effectiveClientId: string | undefined,
  connectedPlatforms: readonly string[] | undefined,
): boolean {
  return !!effectiveClientId && (connectedPlatforms?.length ?? 0) > 0;
}
